if (!customElements.get('bt-favorite-button')) {
  const PROXY_PATH = '/apps/bt-signals/v1/favorites';
  const STATE_BATCH_LIMIT = 100;
  const pendingStateIds = new Set();
  let stateFlushQueued = false;

  const sameVariantButtons = (variantId) =>
    document.querySelectorAll(`bt-favorite-button[data-variant-id="${CSS.escape(variantId)}"]`);

  const applyState = (variantId, favorited) => {
    sameVariantButtons(variantId).forEach((element) => element.setFavorited(favorited));
  };

  const flushStateRequests = async () => {
    stateFlushQueued = false;
    const ids = [...pendingStateIds];
    pendingStateIds.clear();

    for (let i = 0; i < ids.length; i += STATE_BATCH_LIMIT) {
      const chunk = ids.slice(i, i + STATE_BATCH_LIMIT);
      try {
        const url = `${PROXY_PATH}/state?variant_ids=${chunk.join(',')}`;
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) continue;
        const { data } = await response.json();
        Object.entries(data?.variants || {}).forEach(([id, favorited]) => applyState(id, favorited === true));
      } catch (_error) {
        // Hearts stay in the default unfavorited state when state cannot be read.
      }
    }
  };

  const queueStateRequest = (variantId) => {
    pendingStateIds.add(variantId);
    if (stateFlushQueued) return;
    stateFlushQueued = true;
    setTimeout(flushStateRequests, 50);
  };

  const openLoginDialog = (trigger) => {
    const dialog = document.getElementById('BtFavoriteLoginDialog');
    if (!dialog) return;
    dialog.addEventListener('close', () => trigger.focus(), { once: true });
    dialog.showModal();
  };

  class BtFavoriteButton extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.subscribeToVariantChanges();
        return;
      }
      this.initialized = true;

      this.button = this.querySelector('button');
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.loggedIn = this.dataset.loggedIn === 'true';
      if (!this.button || !/^\d+$/.test(this.productId || '') || !/^\d+$/.test(this.variantId || '')) return;

      this.button.addEventListener('click', (event) => this.onClick(event));
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.subscribeToVariantChanges();
      if (this.loggedIn) queueStateRequest(this.variantId);
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
      this.variantChangeUnsubscriber = null;
    }

    subscribeToVariantChanges() {
      if (this.variantChangeUnsubscriber || !this.closest('product-info')) return;
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    handleVariantChange({ data }) {
      const productInfo = this.closest('product-info');
      if (!productInfo || data?.sectionId !== productInfo.dataset.section) return;

      const variantId = String(data.variant?.id || '');
      if (variantId === this.variantId) return;

      this.variantId = variantId;
      this.setFavorited(false);
      if (!/^\d+$/.test(variantId)) {
        delete this.dataset.variantId;
        this.button.disabled = true;
        return;
      }

      this.dataset.variantId = variantId;
      this.button.disabled = false;
      if (this.loggedIn) queueStateRequest(variantId);
    }

    setFavorited(favorited) {
      this.favorited = favorited;
      this.button.setAttribute('aria-pressed', String(favorited));
      this.button.setAttribute('aria-label', favorited ? this.button.dataset.labelRemove : this.button.dataset.labelAdd);
    }

    async onClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.loggedIn) {
        openLoginDialog(this.button);
        return;
      }
      if (this.button.getAttribute('aria-busy') === 'true') return;

      const next = !this.favorited;
      applyState(this.variantId, next);
      this.button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${PROXY_PATH}/${this.variantId}`, {
          method: next ? 'PUT' : 'DELETE',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: next ? { 'Content-Type': 'application/json' } : undefined,
          body: next ? JSON.stringify({ product_id: this.productId }) : undefined,
        });

        if (response.status === 401) {
          applyState(this.variantId, !next);
          openLoginDialog(this.button);
          return;
        }
        if (!response.ok) throw new Error(String(response.status));

        const { data } = await response.json();
        applyState(this.variantId, data?.favorited === true);
      } catch (_error) {
        applyState(this.variantId, !next);
      } finally {
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('bt-favorite-button', BtFavoriteButton);
}
